/**
 * useMAU — MAU (Monthly Active Users) tracking hook.
 * Tracks user activity per gym per month using per-user subcollection docs
 * at gyms/{gymId}/usage/{YYYY-MM}/users/{uid}.
 * Uses getCountFromServer for efficient counting without downloading all docs.
 */
import { useEffect, useState } from "react";
import { doc, setDoc, collection, getCountFromServer, getDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { isClientOutdated } from "./useClientCompat";

// Gym+user+month triples already written in this app session. Cleared by a
// reload, which is deliberate: one write per page load is also the safety net
// for a doc that got deleted (gym cleanup, manual admin fix) mid-month.
const _trackedMonths = new Set();

/** Test seam — lets the suite start from a clean session. */
export function _resetMauTracking() {
  _trackedMonths.clear();
}

/**
 * Track a user as active for the current month.
 * Writes a per-user document to avoid contention on a single usage doc.
 * Safe to call multiple times — setDoc with merge is idempotent per user.
 *
 * Only the FIRST write per gym+user+month carries information: MAU is a
 * `count()` over the subcollection, and the other consumers
 * (`functions/marketSnapshot.js`) read the doc IDs. Nothing reads `lastActive`,
 * so re-writing the same doc is pure traffic — and the hot callers fire it on
 * every single action. `saveAttempt` calls it per JAM save, which at 100
 * competitors × 30 routes means ~6 000 writes where ~100 would do: a third of
 * the write packets every phone in the hall sends, plus enough same-doc write
 * rate for a fast-tapping competitor to queue retries behind the saves that
 * actually matter.
 */
export async function trackMAU(gymId, uid) {
  if (!gymId || !uid) return;
  // Below the live compatibility level the usage docs' shape is governed by a
  // newer contract; skip, and do not mark the month tracked — the updated
  // build writes it after the reload.
  if (isClientOutdated()) return;
  const month = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const key = `${gymId}_${uid}_${month}`;
  if (_trackedMonths.has(key)) return;
  // Marked before the await, so concurrent callers collapse into one write.
  // Rolled back on failure — otherwise a single offline blip would suppress
  // tracking for the rest of the session and silently under-count MAU.
  _trackedMonths.add(key);
  try {
    const userRef = doc(db, `gyms/${gymId}/usage/${month}/users/${uid}`);
    await setDoc(userRef, { uid, lastActive: Timestamp.now() }, { merge: true });
  } catch (err) {
    _trackedMonths.delete(key);
    throw err;
  }
}

/**
 * The current month's MAU of a gym, or `null` when no number can be had.
 *
 * For the CURRENT month it always reads the live per-user subcollection
 * count. The `updateAllGymMauSnapshots` routine refreshes `derived/mau` only
 * once a day at 03:45, so its `currentMau` is stale for the rest of the day —
 * worst on the 1st of a month, when the 03:45 run counts an essentially empty
 * usage collection and the stats card would show 0 until the next night's
 * run, even as users climb. A single count() is one read and fires lazily
 * (only when the stats/subscription section asks), so the cost is negligible.
 * The snapshot doc is only a fallback for when the live count can't be
 * reached. (The platform dashboard still reads the snapshot directly for O(1)
 * cross-gym reads — that's where the denormalization earns its keep.)
 *
 * Shared by `useMAU` and the admin statistics source
 * (`useAdminStatsSources`, docs/spec/01f-tab-loading.md), which keeps the
 * number across section opens instead of counting on every mount.
 */
export async function countCurrentMAU(gymId) {
  if (!gymId) return null;
  const month = new Date().toISOString().slice(0, 7);
  try {
    const usageCol = collection(db, `gyms/${gymId}/usage/${month}/users`);
    const countSnap = await getCountFromServer(usageCol);
    return countSnap.data().count;
  } catch (err) {
    // Fall through to the (possibly stale) snapshot doc — but say so. A
    // silent fallback here renders a number that can be a day old as if it
    // were live; on a permission error it renders one the caller was never
    // allowed to see the fresh version of.
    console.warn("[useMAU] usage count failed, falling back to derived/mau:", err.code, err.message);
  }

  try {
    const snap = await getDoc(doc(db, `gyms/${gymId}/derived/mau`));
    if (snap.exists()) {
      const data = snap.data();
      const fromSnapshot = data.currentMonth === month
        ? data.currentMau
        : (data.history && data.history[month]);
      if (typeof fromSnapshot === "number") return Math.max(0, fromSnapshot);
    }
  } catch { /* noop */ }
  return null;
}

/**
 * Hook to read MAU data for a gym. Fetches the current-month count with
 * getCountFromServer (no standing listener — MAU changes rarely).
 *
 * `refreshKey` lets a caller re-run the count without remounting: pass a
 * value that changes when gym activity happens and the count re-fetches in
 * place. Note: a *distinct* user must become active for the number to move —
 * the same admin logging another ascent is already counted this month, so
 * their own repeat activity won't change MAU. (The admin statistics page no
 * longer uses this hook: its source counts once per open plus one recount per
 * real counter change, see `useAdminStatsSources`.)
 *
 * Returns { currentMAU, loading }.
 */
export function useMAU(gymId, refreshKey) {
  const [currentMAU, setCurrentMAU] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gymId) { setLoading(false); return; }
    let cancelled = false;
    countCurrentMAU(gymId).then((n) => {
      if (cancelled) return;
      // `null` (no live count, no snapshot) keeps the previous number, as before.
      if (typeof n === "number") setCurrentMAU(n);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [gymId, refreshKey]);

  return { currentMAU, loading };
}

/**
 * Hook to read MAU history for the last N months.
 * Uses getCountFromServer for each month (one-time fetch, not a listener).
 * Returns array of { month, mau } sorted descending.
 */
export function useMAUHistory(gymId, monthCount = 6) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gymId) { setLoading(false); return; }
    let cancelled = false;

    // Generate month keys for the last N months. trackMAU writes to
    // the UTC month key (`new Date().toISOString().slice(0, 7)`), so
    // history fetches must use UTC too — building the month from
    // local-zone Date components and then calling toISOString shifts
    // the key into the previous month for any TZ east of UTC
    // (Budapest etc.), which made useMAUHistory pull the wrong months.
    const months = [];
    const now = new Date();
    for (let i = 0; i < monthCount; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      months.push(d.toISOString().slice(0, 7));
    }

    (async () => {
      // Try the snapshot doc first — the scheduled CF stores 12 months of
      // history, so any monthCount up to 12 is served from one read.
      let snapshotHistory = null;
      try {
        const snap = await getDoc(doc(db, `gyms/${gymId}/derived/mau`));
        if (snap.exists()) {
          snapshotHistory = snap.data().history || null;
        }
      } catch { /* noop */ }

      // Always live-count the current month (index 0): the snapshot is
      // refreshed once a day at 03:45, so for "today's" month it's stale
      // (see useMAU above). Past months never change, so they're served
      // straight from the snapshot — only the uncovered ones need count().
      const missing = months.filter(
        (m, i) => i === 0 || !snapshotHistory || snapshotHistory[m] == null
      );

      const fallbackResults = await Promise.all(
        missing.map((month) => {
          const usageCol = collection(db, `gyms/${gymId}/usage/${month}/users`);
          return getCountFromServer(usageCol)
            .then((snap) => ({ month, mau: snap.data().count }))
            .catch(() => ({ month, mau: 0 }));
        })
      );
      const fallbackMap = Object.fromEntries(fallbackResults.map(r => [r.month, r.mau]));

      const merged = months.map((m, i) => ({
        month: m,
        // Current month → live count; past months → snapshot (fallback to count).
        mau: (i > 0 && snapshotHistory && snapshotHistory[m] != null)
          ? snapshotHistory[m]
          : (fallbackMap[m] || 0),
      }));

      if (!cancelled) {
        setHistory(merged);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [gymId, monthCount]);

  return { history, loading };
}

/**
 * Get the current quarter string (e.g., "2026-Q1").
 */
export function getCurrentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `${now.getFullYear()}-Q${q}`;
}

/**
 * Get the start date of the next quarter.
 */
export function getNextQuarterStart() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const nextQMonth = (q + 1) * 3; // 0-based month of next quarter start
  const d = new Date(now.getFullYear(), nextQMonth, 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// `findMauBand` lives in the shared package (the Functions and the e-mail
// package run the same lookup); re-exported so the client keeps importing it here.
export { findMauBand } from "@sendly/shared";
